import * as React from 'react';
import { Link } from 'react-router-dom';
import { projectsData } from '../data/projectsData';
import styles from './Projects.module.css';

interface ProjectsProps {
  lang: 'en' | 'it';
}

/**
 * Projects Component
 * 
 * Displays a list of projects with a featured view.
 * Allows navigating between projects and viewing details and images.
 */
const Projects: React.FC<ProjectsProps> = ({ lang }) => {
  // State to track the currently active project index
  const [activeIndex, setActiveIndex] = React.useState(0);
  const featuredProject = projectsData[activeIndex];

  return (
    <section id="projects" className={styles.projectsSection}>
      <div className={styles.carouselContainer}>
        {/* Header: Number + Title (Left) | Description (Right) */}
        <div className={styles.featuredHeader}>
          <div className={styles.headerLeft}>
            <span className={styles.projectNumber}>{featuredProject.id}</span>
            <Link to={`/project/${featuredProject.id}`} style={{ textDecoration: 'none', color: 'inherit' }}>
              <h2 className={styles.projectTitle}>{featuredProject.title[lang]}</h2>
            </Link>
          </div>
          <div className={styles.headerRight}>
            <p className={styles.projectDescription}>{featuredProject.description[lang]}</p>
          </div>
        </div>

        {/* Center: Horizontal Image Scroll 
            Displays all images/videos for the current project in a scrollable row
        */}
        <div className={styles.featuredImageContainer}>
          {featuredProject.images.map((mediaSrc, idx) => {
            const isVideo = mediaSrc.endsWith('.webm') || mediaSrc.endsWith('.mp4');
            return isVideo ? (
              <video
                key={idx}
                src={mediaSrc}
                autoPlay
                loop
                muted
                playsInline
                className={styles.featuredImage}
              />
            ) : (
              <img
                key={idx}
                src={mediaSrc}
                alt={`${featuredProject.title[lang]} view ${idx + 1}`}
                className={styles.featuredImage}
                loading={idx === 0 ? "eager" : "lazy"}
                decoding="async"
                // @ts-ignore
                fetchpriority={idx === 0 ? "high" : "auto"}
              />
            );
          })}
        </div>

        {/* Footer: Category (Left) | Details (Right) */}
        <div className={styles.featuredFooter}>
          <div className={styles.footerLeft}>
            <h3 className={styles.projectCategory}>{featuredProject.category[lang]}</h3>
          </div>
          <div className={styles.footerRight}>
            {featuredProject.details.map((detail, idx) => (
              <div key={idx} className={styles.detailItem}>
                <span className={styles.detailLabel}>{detail.label[lang]}</span>
                <span className={styles.detailValue}>
                  {typeof detail.value === 'string' ? detail.value : detail.value[lang]}
                </span>
              </div>
            ))}
            <Link to={`/project/${featuredProject.id}`} className={styles.viewProjectLink}>
              {lang === 'en' ? 'View Case Study' : 'Vedi Case Study'} &rarr;
            </Link>
          </div>
        </div>

        {/* Navigation Menu 
            Allows user to switch between different projects
        */}
        <div className={styles.navigation}>
          {projectsData.map((project, idx) => (
            <button
              key={idx}
              className={`${styles.projectMenuItem} ${idx === activeIndex ? styles.activeProjectMenuItem : ''}`}
              onClick={() => setActiveIndex(idx)}
              aria-label={`View ${project.title[lang]}`}
            >
              <span className={styles.projectMenuNumber}>{project.id}</span>
              <span className={styles.projectMenuTitle}>{project.title[lang]}</span>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Projects;
